import { CamelCaseKeys, SessionState, UserStatus } from '@uplift/types';
import { restClient } from '../../client';
import { ValidationError } from '../../errors';
import { Appointment, LegacyUICalendarEventAppointment } from '../../resources';


export type CalendarAppointmentsInput = {
  datetime_from?: string;
  datetime_to: string;
  limit?: number;
  states?: SessionState[];
} | {
  datetime_from?: string;
  datetime_to?: string;
  limit: number;
  states?: SessionState[];
};

export type CalendarAppointmentPatient = {
  id: string;
  status: UserStatus;
  first_name: string;
  preferred_name?: string;
  last_name: string;
};

export type CalendarAppointmentsOutput = {
  patients: Record<string, CalendarAppointmentPatient>;
  appointments: LegacyUICalendarEventAppointment[];
};

export const calendarAppointments = (params: CalendarAppointmentsInput) => restClient.rawRequest<CalendarAppointmentsOutput>('/api/v1/calendar/get_appointments', {
  method: 'POST',
  data: params,
});


export type CalendarBookingSlotsInput = {
  datetime_from: string;
  datetime_to: string;
};

export type CalendarBookingSlotsOutput = {
  available_slots: string[];
};

export const calendarBookingSlots = (params: CalendarBookingSlotsInput) => restClient.rawRequest<CalendarBookingSlotsOutput>('/api/v1/calendar/booking-slots', {
  method: 'GET',
  params: params,
});


export type CalendarSuggestedFollowUpInput = {
  session_start: string;
  interval_days: number;
};

export type CalendarSuggestedFollowUpOutput = {
  data: {
    suggested_at: string;
  };
};

export const calendarSuggestedFollowUp = (params: CalendarSuggestedFollowUpInput) => restClient.request<CalendarSuggestedFollowUpOutput>('/api/v1/calendar/suggested-follow-up', {
  method: 'GET',
  params: params,
});


export type CalendarBookedDaysInput = {
  datetime_from: string;
  datetime_to: string;
};

export type CalendarBookedDaysOutput = {
  [key: string]: boolean;
};

export const calendarBookedDays = (params: CalendarBookedDaysInput) => restClient.rawRequest<CalendarBookedDaysOutput>('/api/v1/calendar/booked-days', {
  method: 'GET',
  params: params,
});


export const calendarVerifyICS = (url: string) =>
  restClient.request<{ data: { valid: true; title: string } | { valid: false } }>('/api/v1/calendar/ics/verify', { method: 'POST', data: { url } });


export type CalendarSubscribeToICSError = (
  | { error: string; error_key: 'already_subscribed' }
  | ValidationError<Record<'url', string[]>>
);

export const calendarSubscribeToICS = (url: string) =>
  restClient.request<{ data: { title: string; url: string } }>('/api/v1/calendar/ics/subscribe', { method: 'POST', data: { url } });

export const calendarUnsubscribeFromICS = () =>
  restClient.request('/api/v1/calendar/ics/unsubscribe', { method: 'POST' });


export type ICSSubscriptionDetails = {
  title: string;
  url: string;
  updatedAt: string;
};

export const calendarGetICSSubscription = () =>
  restClient.request<{ data: ICSSubscriptionDetails[] }>('/api/v1/calendar/ics');


export const calendarRefreshICSSubscription = () =>
  restClient.request<{ data: null }>('/api/v1/calendar/ics/refresh', { method: 'POST' });


export type ICSEvent = CamelCaseKeys<{
  id: string;
  title: string;
  description: string | null;
  timezone: string;
  from_datetime: string;
  to_datetime: string;
  all_day: boolean;
  rrule?: string;
}>;

export type ICSEventConflict = CamelCaseKeys<{
  ics_event_id: string;
  appointment: Appointment;
}>;

export const calendarGetICSEvents = () => restClient.request<{
  data: {
    events: ICSEvent[];
    conflicts: ICSEventConflict[];
  };
}>('/api/v1/calendar/ics/events');


export const ignoreICSConflict = (icsEventId: string, appointmentId: string) => restClient.request<null>('/api/v1/calendar/ics/ignore_conflict', {
  method: 'POST',
  data: {
    ics_event_id: icsEventId,
    appointment_id: appointmentId,
  }
});
